/**
 * Client-side consent state, backed by the cookie that
 * @beyonk/gdpr-cookie-consent-banner writes.
 *
 * PitchVault has exactly two categories:
 *
 *  - necessary: the founder's login cookie and the viewer's session cookie. The
 *    viewer session identifies the browser that was granted a signed URL for a
 *    private document, so it is required to deliver the document the visitor
 *    asked for. Not opt-outable, and exempt from consent under ePrivacy.
 *  - analytics: per-page read timing. This is the part that profiles behaviour,
 *    so it is off until the visitor opts in, and the tracker does not run
 *    without it.
 */

export const CONSENT_COOKIE = 'pitchvault_gdpr';

/** Fired on window by the banner whenever the analytics choice is set. */
export const ANALYTICS_EVENT = 'consent:analytics';

export type ConsentChoices = {
  necessary?: boolean;
  analytics?: boolean;
  [key: string]: unknown;
};

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * The stored choices, or null when the visitor has not answered the banner yet.
 *
 * The banner writes base64-encoded JSON shaped as `{ choices: {...}, fp?: "..." }`.
 * Both the encoding and the nesting are decoded here; plain JSON is also accepted
 * so a future change to the library's storage format degrades to "no consent"
 * rather than to a crash.
 */
export function readConsent(): ConsentChoices | null {
  const raw = readCookie(CONSENT_COOKIE);
  if (!raw) return null;

  const candidates: string[] = [raw];
  try {
    candidates.push(atob(raw));
  } catch {
    /* not base64 */
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate) as { choices?: ConsentChoices } & ConsentChoices;
      if (typeof parsed !== 'object' || parsed === null) continue;
      const choices = parsed.choices ?? parsed;
      if (typeof choices === 'object' && choices !== null) return choices as ConsentChoices;
    } catch {
      /* try the next decoding */
    }
  }
  return null;
}

/** True only on an explicit opt-in. Absent or unreadable consent means no. */
export function hasAnalyticsConsent(): boolean {
  return readConsent()?.analytics === true;
}

/**
 * Subscribe to analytics consent changes. Calls back with the new value whenever
 * the visitor accepts or rejects. Returns an unsubscribe function.
 */
export function onAnalyticsConsentChange(cb: (granted: boolean) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ agreed?: boolean }>).detail;
    // Fall back to the cookie if the event carries no detail.
    cb(detail?.agreed ?? hasAnalyticsConsent());
  };
  window.addEventListener(ANALYTICS_EVENT, handler);
  return () => window.removeEventListener(ANALYTICS_EVENT, handler);
}
